import { useRef, useEffect, useState } from "react";
import { PlanetRenderer } from "./engine/planet";
import { AssemblyRenderer } from "./engine/assembly";
import { EmbiGlitch } from "./engine/glitch";
import { seedCountdown, fmtDuration, tickCountdown, flickerString, STATUS_WORDS } from "./engine/countdown";
import { formatClock } from "./engine/readouts";

const G = {
  deg: "°",
  mid: "·",
  ring: "◦",
  diamond: "◆",
  star: "★",
  halfcirc: "◐",
  play: "▶",
  check: "✓",
  bar: "▮",
  mdash: "—",
};

export interface ComingSoonProps {
  accent?: string;
  glitchIntensity?: number;
  showCountdown?: boolean;
}

export function ComingSoon({ accent = "#c8ff32", glitchIntensity = 0.5, showCountdown = true }: ComingSoonProps) {
  const planetRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<HTMLCanvasElement>(null);
  const ledRef = useRef<HTMLSpanElement>(null);
  const clockRef = useRef<HTMLSpanElement>(null);
  const statusRef = useRef<HTMLSpanElement>(null);
  const countRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const coordRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const embiRef = useRef<HTMLSpanElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const honeyRef = useRef<HTMLInputElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);

  const [submitted, setSubmitted] = useState(false);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);

  // hydrate from a prior signup (server remains source of truth)
  useEffect(() => {
    try {
      const s = localStorage.getItem("embiverse_notify");
      if (s) {
        const o = JSON.parse(s);
        if (o && o.pos != null) {
          setSubmitted(true);
          setQueuePosition(o.pos);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // ---- animation engine (imperative; never triggers React re-renders) ----
  useEffect(() => {
    const reduced =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;
    const gi = glitchIntensity;
    const timers: Array<ReturnType<typeof setInterval>> = [];
    let raf = 0;
    let glitch: EmbiGlitch | null = null;

    const planet = planetRef.current ? new PlanetRenderer(planetRef.current) : null;
    const assembly = glRef.current ? new AssemblyRenderer(glRef.current) : null;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const sizeAll = () => {
      planet?.resize(dpr);
      assembly?.resize();
    };
    let webgl = false;
    if (assembly) webgl = assembly.init();

    // Fail-safe: the WebGL background canvas sits above the dark page (z-index 1).
    // If WebGL can't initialize or its context is lost, an unrendered canvas can
    // composite as an opaque (light) surface, covering the dark theme entirely.
    // Whenever WebGL isn't actively drawing, hide the canvas so the dark page shows.
    const bg = glRef.current;
    const hideBg = () => {
      if (bg) bg.style.visibility = "hidden";
    };
    const onCtxLost = (e: Event) => {
      e.preventDefault();
      hideBg();
    };
    if (!webgl) hideBg();
    bg?.addEventListener("webglcontextlost", onCtxLost);

    sizeAll();

    const tickClock = () => {
      if (clockRef.current) clockRef.current.textContent = formatClock(new Date());
    };
    tickClock();

    let cd = seedCountdown();
    let cdStr = fmtDuration(cd);
    const renderCount = (text: string, err: boolean) => {
      if (!countRef.current) return;
      countRef.current.textContent = text;
      countRef.current.style.color = err ? "#ff6a4d" : accent;
    };
    renderCount(cdStr, false);

    if (embiRef.current) {
      glitch = new EmbiGlitch(embiRef.current, { accent, glitchIntensity: gi });
      if (reduced) glitch.rest();
      else glitch.intro();
    }

    let barIx = 0,
      lat = 48.8566,
      lon = 2.3522;
    const tickSlow = () => {
      if (statusRef.current && Math.random() < 0.5)
        statusRef.current.textContent = STATUS_WORDS[(Math.random() * STATUS_WORDS.length) | 0];
      if (barRef.current) {
        barIx = (barIx + 1) % 13;
        const cells = barRef.current.children;
        for (let i = 0; i < cells.length; i++) {
          const on = i < barIx || Math.random() < 0.06;
          (cells[i] as HTMLElement).style.background = on ? accent : "transparent";
        }
      }
      if (coordRef.current) {
        lat += (Math.random() - 0.5) * 0.02;
        lon += (Math.random() - 0.5) * 0.02;
        coordRef.current.textContent = `LAT ${lat.toFixed(4)}° · LON ${lon.toFixed(4)}°`;
      }
    };

    if (reduced) {
      // Settle the assembly into its formation instantly (no visible motion), then paint
      // the identical static frame across the first ~30 frames so it survives the canvas
      // layout-timing race, then stop. Re-rendering identical pixels is not "motion".
      if (webgl) for (let s = 0; s < 90; s++) assembly?.step(1);
      let n = 0;
      const paintStatic = () => {
        sizeAll();
        planet?.draw(2, accent);
        if (webgl) assembly?.step(1);
        if (++n < 30) raf = requestAnimationFrame(paintStatic);
      };
      raf = requestAnimationFrame(paintStatic);
      renderCount(cdStr, false);
    } else {
      timers.push(setInterval(tickClock, 1000));
      timers.push(
        setInterval(() => {
          cd = tickCountdown(cd, gi);
          cdStr = fmtDuration(cd);
        }, 1000),
      );
      timers.push(setInterval(() => renderCount(flickerString(cdStr, gi), Math.random() < 0.03 + 0.09 * gi), 80));
      timers.push(setInterval(tickSlow, 420));
      const loop = (now: number) => {
        const t = now / 1000;
        planet?.draw(t, accent);
        if (webgl) assembly?.step(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    const onResize = () => sizeAll();
    window.addEventListener("resize", onResize);

    return () => {
      timers.forEach(clearInterval);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      bg?.removeEventListener("webglcontextlost", onCtxLost);
      glitch?.stop();
      assembly?.dispose();
    };
  }, [accent, glitchIntensity]);

  // ---- subscribe ----
  const setHint = (text: string, err: boolean) => {
    const h = hintRef.current;
    if (h) {
      h.textContent = text;
      h.style.color = err ? "#ff6a4d" : "";
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const el = emailRef.current;
    if (!el) return;
    const v = (el.value || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      setHint("✗ MALFORMED ADDRESS — CHECK & RETRY.", true);
      el.style.color = "#ff6a4d";
      return;
    }
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: v, website: honeyRef.current?.value ?? "" }),
      });
      const data = (await res.json().catch(() => ({}))) as { position?: number; error?: string };
      if (res.ok || res.status === 409) {
        const pos = typeof data.position === "number" ? data.position : null;
        try {
          localStorage.setItem("embiverse_notify", JSON.stringify({ email: v, pos }));
        } catch {
          /* ignore */
        }
        setQueuePosition(pos);
        setSubmitted(true);
        if (liveRef.current) liveRef.current.textContent = "Signal registered — you're on the launch notification list.";
        (window as unknown as { plausible?: (e: string) => void }).plausible?.("subscribe");
      } else if (res.status === 400 && data.error === "invalid_email") {
        setHint("✗ MALFORMED ADDRESS — CHECK & RETRY.", true);
        el.style.color = "#ff6a4d";
      } else if (res.status === 429) {
        setHint("✗ TOO MANY SIGNALS — WAIT A MOMENT.", true);
      } else if (res.status === 503) {
        setHint("✗ SIGNAL OFFLINE — TRY AGAIN LATER.", true);
      } else {
        setHint("✗ SIGNAL LOST — RETRY.", true);
      }
    } catch {
      setHint("✗ SIGNAL LOST — RETRY.", true);
    }
  };

  const queuePosStr = queuePosition != null ? "#" + String(queuePosition).padStart(4, "0") : "#0000";

  return (
    <section className="screen" style={{ ["--accent" as string]: accent } as React.CSSProperties}>
      <canvas ref={glRef} className="bg-canvas" aria-hidden="true" />

      <div className="topbar">
        <span className="brand">
          {G.ring} EMBIVERSE<span className="io">.IO</span>
        </span>
        <div className="topbar-right">
          <span className="status-grp">
            <span ref={ledRef} className="led" />
            <span className="sm-hide">SYSTEM: WARMING UP</span>
          </span>
          <span className="dim55 sm-hide">PRE-LAUNCH</span>
          <span className="dim55 sm-hide">v0.1</span>
          <span ref={clockRef} className="clock">
            --:--:--
          </span>
        </div>
      </div>

      <div className="ticker" aria-hidden="true">
        <div className="ticker-track">
          {[0, 1].map((dup) => (
            <span key={dup}>
              <span>{G.diamond} SOMETHING IS TAKING SHAPE</span>
              <span className="lime">COMING SOON</span>
              <span>STAND BY FOR SIGNAL</span>
              <span className="pur">EMBIVERSE // MMXXVI</span>
              <span>{G.diamond} PRE-LAUNCH SEQUENCE ENGAGED</span>
              <span className="cyan">THE ORBIT IS BEING CALIBRATED</span>
            </span>
          ))}
        </div>
      </div>

      <main className="main">
        <div className="kicker">[ /SYS/EMBIVERSE ] &nbsp; STATUS: PRE-LAUNCH &nbsp;//&nbsp; SIGNAL ACQUIRED</div>

        <div className="planet-box">
          <canvas ref={planetRef} aria-hidden="true" />
        </div>

        <h1 className="wordmark">
          <span ref={embiRef} className="embi">
            EMBI
          </span>
          VERSE<span className="io">.IO</span>
          <span className="cursor" />
        </h1>

        <div className="cs-block">
          <span className="badge">{G.star} COMING SOON</span>
          <p className="tagline">
            A new universe is being assembled. No details yet {G.mdash} just coordinates. Leave a signal and we{"’"}ll
            wake you when the orbit locks.
          </p>
        </div>

        {showCountdown && (
          <div className="countdown">
            <div className="cd-head">
              <span className="lbl">{G.halfcirc} T-MINUS.EXE</span>
              <span ref={statusRef} className="st">
                RECALIBRATING
              </span>
            </div>
            <div ref={countRef} className="cd-digits">
              00:00:00:00
            </div>
            <div className="cd-subs">
              <span>DAYS</span>
              <span>HRS</span>
              <span>MIN</span>
              <span>SEC</span>
            </div>
          </div>
        )}

        <div className="email-wrap">
          {!submitted ? (
            <>
              <form className="email-form" onSubmit={onSubmit}>
                <label className="sr-only" htmlFor="email">
                  Email address for launch notification
                </label>
                <input
                  ref={emailRef}
                  id="email"
                  className="email-input"
                  type="email"
                  placeholder="you@embiverse.io"
                  autoComplete="email"
                />
                <input
                  ref={honeyRef}
                  className="honeypot"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  name="website"
                />
                <button className="email-btn" type="submit">
                  {G.play} NOTIFY ME
                </button>
              </form>
              <div ref={hintRef} className="email-hint">
                // WE{"’"}LL TRANSMIT ONE SIGNAL WHEN EMBIVERSE GOES LIVE.
              </div>
            </>
          ) : (
            <div className="success">
              <div className="success-title">{G.check} SIGNAL REGISTERED</div>
              <div className="success-body">
                YOU{"’"}RE <span className="pos">{queuePosStr}</span> IN THE QUEUE. STAND BY {G.mdash} WE{"’"}LL REACH YOU
                WHEN THE ORBIT LOCKS.
              </div>
            </div>
          )}
          <div ref={liveRef} className="sr-only" aria-live="polite" />
        </div>
      </main>

      <div className="hud hud-left" aria-hidden="true">
        <div className="boot">
          &gt; BOOTSTRAPPING REALITY
          <span className="boot-cursor">_</span>
        </div>
        <div ref={barRef} className="loader">
          {Array.from({ length: 12 }).map((_, i) => (
            <i key={i} />
          ))}
        </div>
        <div ref={coordRef}>
          LAT 48.8566{G.deg} {G.mid} LON 2.3522{G.deg}
        </div>
      </div>

      <div className="hud hud-right" aria-hidden="true">
        <div>
          NODE 0x7F3A {G.mid} SIGNAL <span className="sig-on">{G.bar}{G.bar}{G.bar}</span>
          <span className="sig-off">{G.bar}{G.bar}</span>
        </div>
        <div className="boot">{"©"} MMXXVI EMBIVERSE.IO {G.mdash} ALL SYSTEMS NOMINAL</div>
      </div>

      <div className="overlay vignette" />
      <div className="overlay scanlines" />
    </section>
  );
}
